/**
 * The pharmacy module catalogue — the hub's tile banks, and nothing else.
 *
 * Lifted out of PharmacyHubPage on 2026-08-25 so the shared Header can ask
 * "is this route a pharmacy module?" without importing the page that renders
 * the Header, which would be a cycle. Same reason approvedSuppliers and
 * buildingMaintenance keep their category data in a sibling module.
 *
 * This list is the ONE place the catalogue lives. A module added to a bank
 * here appears on the hub AND gets its way back to the hub, together, so the
 * two cannot drift.
 */

import { clinicalModuleForWebRoute } from '@jeanieiq/types';

export interface Tile { to: string; labelKey: string; icon: string }
export interface Figure { pre?: string; value: string; unit?: string }
export interface Category {
  id: string; label: string; blurb: string; icon: string;
  tiles?: Tile[]; href?: string; // href = single-module category, links straight through
  /**
   * A LIVE figure — open handovers, connected accounts. Omit it on a tile bank
   * and the hub counts the tiles instead.
   *
   * WHY (2026-09-01). Four banks carried a hardcoded count and every one had
   * drifted as modules were added: Clinical said 9 with 14 behind it, Care
   * Homes said 3 with 8, Governance said 11 with 14, Dispensing 9 with 10.
   * A hand-maintained count of a list sitting directly beneath it is a number
   * nobody can check against anything, so it silently stops being true.
   *
   * It could not have been right in any case: the hub filters tiles by the
   * branch's nation, so the honest count differs between an English and a Welsh
   * branch of the same tenant. Only a derived one can say both.
   */
  /*
   * Return NULL to decline (2026-09-08). The hub then falls back to counting
   * the tiles, exactly as a bank with no figure does.
   *
   * WHY A FIGURE NEEDS TO BE ABLE TO SAY NOTHING. `accounts` counts Sage-
   * migrated accounts, and almost no tenant runs a Sage migration — so the
   * landing page showed "0 ACCOUNTS" at 24 point, as the first number a
   * visitor sees. That zero is true about the migration and false about the
   * module: Daily Takings works perfectly without ever importing Sage. A live
   * figure is worth more than a derived one only while it has something live
   * to say; below that it is worth less than nothing, because a zero on a
   * landing page reads as an empty product.
   */
  figure?: (f: Figures) => Figure | null;
  /** The word for the derived count. Defaults to 'modules'. */
  countUnit?: string;
}
export interface Figures {
  /* `accounts` left with the Accounts bank on 2026-09-25 — see the foot of
     CATEGORIES. A figure nothing reads is a query nobody needed. */
  handoverOpen: number; queryOpen: number;
}

/**
 * CLINICAL MODULES ARE HIDDEN, PER MODULE (Kris, 2026-10-01; clinical safety
 * case CS-02, HM-01). A page belonging to a clinical module (CLINICAL_MODULES in
 * @jeanieiq/types, the list the API and Platform Admin also read) is offered
 * only when a platform admin has switched that module on for the tenant. The
 * API refuses the rest regardless; this only stops a tile leading to a 404.
 */
export function pageOfferedTo(route: string, enabledModules: readonly string[] | undefined): boolean {
  const mod = clinicalModuleForWebRoute(route);
  return mod === null || (enabledModules ?? []).includes(mod);
}

export const CATEGORIES: Category[] = [
  /*
   * THE ORDER IS REAGAN'S, 2026-09-29: "the order of tabs at the top needs to
   * be governance and quality, clinical ervices dispensing and dc the
   * insidhgts and ops".
   *
   * It is held here rather than at the two render sites — the hub's tile page
   * and the strips' top tab bar — because both show the same four names and
   * must agree. An order kept anywhere else is an order one of them can be
   * written without.
   */
  {
    id: 'governance', label: 'Governance & Quality', blurb: 'Watchtower, SOPs, IG, premises & the vault', icon: 'shield',
    countUnit: 'registers',
    tiles: [
      { to: '/watchtower', labelKey: 'watchtower', icon: 'traffic' },
      { to: '/premises', labelKey: 'premises', icon: 'hospital' },
      { to: '/rp-log', labelKey: 'rp_log', icon: 'user' },
      /*
       * IT STAYS HERE — DECIDED, NOT DEFERRED (Reagan, 2026-09-30).
       *
       * This note used to say "TO MOVE — NOT YET": that the Near-miss Log
       * belonged in HEALTH & SAFETY because a near miss is a near miss whatever
       * the business does, and that the only thing stopping the move was the
       * sector-conditional entry H&S would need
       * (specs/18-sector-driven-sidebar-and-overrides.md) so a joinery firm's
       * H&S section would not grow a dispensing near-miss log.
       *
       * That reading was put to Reagan and rejected. A DISPENSING near miss is
       * not the same animal as a workplace one — it is a wrong drug, a wrong
       * strength or a wrong patient caught before handout, it is governed by
       * the GPhC rather than the HSE, and the people who record it and review
       * it are the pharmacy team sitting in this section. Filing it beside slips
       * and trips would put it in front of the wrong reviewers.
       *
       * So the sector lever is NOT a prerequisite for anything here, and the
       * tile is not waiting on it. Do not re-derive the move from first
       * principles: the argument for it is written above and was answered.
       */
      // SI 2025/1249 (Kris, 2026-09-26): who a pharmacist has authorised, where.
      { to: '/pharmacist-authorisations', labelKey: 'pharmacist_authorisations', icon: 'signature' },
      { to: '/yellow-card', labelKey: 'yellow_card', icon: 'certificate' },
      { to: '/cas', labelKey: 'cas', icon: 'megaphone' },
      { to: '/ig-governance', labelKey: 'ig_governance', icon: 'shield' },
      /*
       * DOCUMENT VAULT REMOVED FROM THE HUB (Reagan, 2026-09-25). A pharmacy
       * keeping its documents somewhere the platform's Document Register cannot
       * see them is two registers, and an inspector asked for one. It returns
       * when the vault feeds the register.
       */
      /*
       * GPhC & SMARTCARDS MOVED TO HR & PEOPLE (Reagan, 2026-10-26: "the gphc
       * and smartcards need going in the hr and people. not in the pharmacy
       * section"). A registration belongs to an employee, so it is kept where
       * the rest of that person's record is. The route still works.
       */
      { to: '/carers', labelKey: 'carers', icon: 'wheelchair' },
      // PH-SHELL-29 (Kris, 2026-09-18).
      { to: '/compliance', labelKey: 'ig_compliance', icon: 'receipt' },
    ],
  },
  {
    id: 'clinical', label: 'Clinical Services', blurb: 'Advanced & enhanced services', icon: 'stethoscope',
    countUnit: 'services',
    tiles: [
      { to: '/service-opportunities', labelKey: 'service_opportunities', icon: 'target' },
      { to: '/pharmacy-first', labelKey: 'pharmacy_first', icon: 'stethoscope' },
      { to: '/pips', labelKey: 'pips', icon: 'stethoscope' },
      { to: '/contraception', labelKey: 'contraception', icon: 'pill' },
      { to: '/nms', labelKey: 'nms', icon: 'pill' },
      { to: '/pmr', labelKey: 'pmr', icon: 'idBadge' },
      { to: '/vaccinations', labelKey: 'vaccinations', icon: 'vaccine' },
      { to: '/ems', labelKey: 'ems', icon: 'urgent' },
      { to: '/smoking-cessation', labelKey: 'smoking_cessation', icon: 'smokingNo' },
      { to: '/hypertension', labelKey: 'hypertension', icon: 'heartMonitor' },
      { to: '/anticoag', labelKey: 'anticoag', icon: 'droplet' },
      { to: '/pcn', labelKey: 'pcn_liaison', icon: 'hospital' },
    ],
  },
  {
    id: 'dispensing', label: 'Dispensing & CD', blurb: 'Tariff, stock, CD register & oversight', icon: 'pill',
    countUnit: 'modules',
    tiles: [
      /*
       * PMR IMPORT LEFT THIS BANK (Reagan, 2026-10-30: *"move pmr import
       * there"*). It is not dispensary work — it is how a patient list
       * arrives — so it lives on Pharmacy's own Import / Export screen with
       * the other migration panels.
       *
       * /pmr-import still answers on its own address. Reached directly it
       * now renders without the bank strip, because it no longer belongs to
       * a bank; the sidebar's Import / Export row stays lit on it.
       */
      { to: '/dispensary-activity', labelKey: 'dispensary_activity', icon: 'chartLine' },
      /*
       * NEAR-MISS AND FRIDGE TEMPERATURES CAME OVER FROM GOVERNANCE & QUALITY
       * (Reagan, 2026-10-31). Both are governance records, which is why they
       * sat there, but both are kept BY the dispensary AS it dispenses: a
       * near miss is caught at the bench and a fridge is checked on the round.
       * Filing them where the work happens beats filing them by what they
       * prove.
       */
      { to: '/near-miss', labelKey: 'near_miss', icon: 'alert' },
      { to: '/fridge-temps', labelKey: 'fridge_temps', icon: 'thermometer' },
      /*
       * PHARMACY STOCK REMOVED FROM THE HUB (Reagan, 2026-09-25). The platform
       * already has Stock Management; a pharmacy keeping its stock in a second
       * place is the duplication rather than the feature. It returns when
       * pharmacy stock talks to normal stock, not before.
       */
      { to: '/drug-tariff', labelKey: 'drug_tariff', icon: 'chartLine' },
      { to: '/service-fees', labelKey: 'service_fees', icon: 'money' },
      { to: '/cd-register', labelKey: 'cd_register', icon: 'lock' },
      { to: '/supervised', labelKey: 'supervised', icon: 'flask' },
      { to: '/fp34c', labelKey: 'fp34c', icon: 'file' },
      { to: '/script-watch', labelKey: 'script_watch', icon: 'eye' },
      { to: '/welsh-dmr', labelKey: 'welsh_dmr', icon: 'flag' },
    ],
  },
  {
    id: 'insights', label: 'Insights & Ops', blurb: 'Analytics, wallboards, handover, queries', icon: 'chartBar',
    figure: (f) => ({ value: String(f.handoverOpen + f.queryOpen), unit: 'open' }),
    tiles: [
      { to: '/insights', labelKey: 'insights', icon: 'chartBar' },
      /*
       * NHS INCOME (Kris, 2026-10-20): straight to the Performance view on the
       * FP34C page - each branch's NHS income month by month, services and all -
       * so a branch or area manager finds what they can push (Pharmacy First,
       * Common Ailments, NMS) from Pharmacy. The page itself stays in Accounts,
       * one page and one set of figures; this is a way in, not a second copy.
       */
      { to: '/fp34c?view=performance', labelKey: 'nhs_income', icon: 'chartLine' },
      { to: '/wallboard-admin', labelKey: 'wallboards', icon: 'screen' },
      { to: '/handover', labelKey: 'handover', icon: 'handover' },
      { to: '/query-log', labelKey: 'query_log', icon: 'question' },
    ],
  },

  /* ── Banks that used to be here, and why they are not ──────────────── */
  /*
   * CARE HOMES LEFT THE HUB (Reagan, 2026-09-25) and is a sidebar section of
   * its own — see CARE_HOME_MODULES in layout/Sidebar.tsx, which is now the one
   * list of these nine screens.
   *
   * WHY. The sidebar link into this hub is labelled "Dispensary Management".
   * Care-home work is not dispensary management, and burying it one level
   * inside something called that meant nobody supplying homes could find their
   * own screens without knowing they were in there. Nine screens behind a name
   * that does not describe them is a worse fault than the duplication that
   * started this tidy-up.
   *
   * These nine keep their routes, their pages and their rows. Only where they
   * are reached from changed, and the sidebar reaches them directly now rather
   * than one click further in.
   */
  /*
   * TEAM REMOVED (Reagan, 2026-09-25) — it was a single link to
   * /pharmacy-rota, and the platform already has Shifts & Timesheets. Pharmacy
   * shifts are to be sorted onto that rota rather than run beside it, so the
   * bank goes now and the work happens later.
   *
   * It supersedes PH-SHELL-29 (Kris, 2026-09-18), which put the pharmacy rota
   * under Team here, and the 2026-08-14 retirement of the 'Workforce & Locum'
   * bank that preceded it. The locum ledger, locum portal and recruitment keep
   * their direct sidebar links — nothing about those changed.
   */
  /*
   * ACCOUNTS REMOVED (Reagan, 2026-09-25). Daily Takings and the Sage 50
   * staging are money, and money already has a home on this platform in ERP &
   * Accounts. A pharmacy posting its takings somewhere the ledger cannot see
   * them is the duplication. The bank returns when these feed Accounts.
   *
   * Its live figure went with it — see PharmacyHubPage, where the trial-balance
   * query is now gone. Nothing else read it.
   */
];

/**
 * Every route reachable from the hub, including the single-module categories
 * that link straight through.
 *
 * WHY THIS EXISTS. The pharmacy vertical is the one section whose modules are
 * NOT in the sidebar - the hub is the only way in (2026-08-13, when the
 * catalogue passed ~40 modules). So on /drug-tariff or /blister-specials there
 * was nothing on screen pointing back, and the only ways out were the browser
 * button or picking a different section entirely. Every other section survives
 * without one because the sidebar still holds its entry.
 */
export const PHARMACY_MODULE_PATHS: ReadonlySet<string> = new Set(
  CATEGORIES.flatMap((c) => [
    ...(c.href ? [c.href] : []),
    ...(c.tiles ?? []).map((t) => t.to),
  ]),
);

/**
 * Which bank a module belongs to — so the way back can be to the bank the
 * person actually came through, not the top of the hub.
 *
 * SUB-ROUTES COUNT (2026-09-01). This was an exact `Set.has(pathname)`, so a
 * module with children lost its way back the moment anybody drilled in:
 * `/care-home-cycles` matched and `/care-home-cycles/<id>` did not, which is
 * exactly where somebody is deepest and most wants the way out. Matching a
 * module path or anything beneath it fixes that.
 *
 * The trailing slash matters: `/pharmacy-first` must not be treated as a child
 * of `/pharmacy`, so a prefix only counts when the next character is a `/`.
 */
/*
 * A SCREEN THAT IS A TAB OF A TILE still belongs to that tile's bank, or the
 * module strip vanishes from an address people have bookmarked. Each entry
 * names the tile it is a tab of, so it cannot outlive it.
 */
const TAB_OF: Readonly<Record<string, string>> = {
  '/pcs': '/contraception',
  '/ehc': '/contraception',
  '/pf-triage': '/pharmacy-first',
};

export function categoryForModulePath(pathname: string): Category | null {
  const tile = TAB_OF[pathname];
  const owns = (base: string): boolean =>
    pathname === base || pathname.startsWith(`${base}/`) || base === tile;
  for (const c of CATEGORIES) {
    if (c.href && owns(c.href)) return c;
    if ((c.tiles ?? []).some((t) => owns(t.to))) return c;
  }
  return null;
}

/*
 * CATALOGUED IN THE HUB, BUT NOT A PHARMACY SECTION SCREEN (Reagan, 2026-10-06).
 *
 * FP34C is the monthly NHS claim, and it is reached through Accounts rather
 * than Pharmacy. Sidebar.tsx moved it under ERP & Accounts on purpose, so an
 * accounts-only user can get to it: "holding Pharmacy would hand them every
 * clinical register". Drawing the banks around it handed them a map of those
 * registers anyway - four bank tabs and the whole Dispensing & CD list - on a
 * page whose own breadcrumb reads Finance. The page is the claim, nothing else.
 *
 * It stays a TILE, so a pharmacy user still finds it where they expect, and it
 * stays a pharmacy module PATH, so Header still draws the branch picker: an
 * FP34C claim belongs to a branch and Fp34cPage reads which one. What it loses
 * is the strips, and its place in BANKED_MODULE_PATHS, which is defined as the
 * routes that render inside them.
 *
 * Matched on the exact path. A module with pages beneath it would need a
 * prefix test, and this one has none.
 */
export const CATALOGUED_BUT_OWN_SCREEN: ReadonlySet<string> = new Set(['/fp34c']);

/**
 * Every route that renders inside the bank strips.
 *
 * Compared WITHOUT its query: the NHS income tile is `/fp34c?view=performance`,
 * the FP34C page opened at its Performance view, and it is as much an Accounts
 * screen as the plain `/fp34c` tile.
 *
 * Derived, not written: `categoryForModulePath` answers the same question one
 * path at a time, and a second hand-kept list of forty-odd routes would be
 * wrong within a week of the next module landing.
 */
export const BANKED_MODULE_PATHS: readonly string[] = CATEGORIES.flatMap((c) => [
  ...(c.href ? [c.href] : []),
  ...(c.tiles ?? []).map((t) => t.to),
]).filter((p) => !CATALOGUED_BUT_OWN_SCREEN.has(p.split('?')[0]!));

/** The hub itself is not one of its own modules, so it gets no back link. */
export function isPharmacyModulePath(pathname: string): boolean {
  return categoryForModulePath(pathname) !== null;
}
